import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSettings, FiTool, FiShield, FiAward, FiUsers, FiTrendingUp, FiCheckCircle, FiArrowRight } = FiIcons;

const ServicesPage = () => {
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      icon: FiSettings,
      title: 'Heavy Engineering & Fabrication',
      shortDesc: 'Large-scale fabrication projects for mining and infrastructure applications',
      fullDesc: 'Our heavy engineering division specialises in large-scale fabrication projects for mining and infrastructure applications. Capabilities include structural steelwork, pressure vessel manufacturing, conveyor systems, and custom industrial equipment.',
      features: [
        'Structural steelwork up to 500 tons',
        'Pressure vessel manufacturing (ASME compliant)',
        'Conveyor systems and material handling',
        'Custom industrial equipment design',
        'Headgear refurbishment and maintenance',
        'Bridge components for infrastructure'
      ],
      standards: ['SANS Specifications', 'ASME Standards', 'AWS Welding Codes', 'API Requirements']
    },
    {
      icon: FiTool,
      title: 'Precision Machining & CNC Operations',
      shortDesc: 'State-of-the-art CNC machining with tolerances to ±0.005mm',
      fullDesc: 'State-of-the-art CNC machining centres enable production of complex components with tolerances to ±0.005mm. Our machining capabilities include 5-axis milling, precision turning, grinding operations, and specialised boring applications.',
      features: [
        '5-axis CNC milling capabilities',
        'Precision turning and grinding',
        'Tolerances to ±0.005mm',
        'Coordinate measuring verification',
        'Exotic materials expertise',
        'Optical inspection systems'
      ],
      standards: ['ISO Tolerances', 'GD&T Standards', 'Material Certifications', 'Dimensional Verification']
    },
    {
      icon: FiShield,
      title: 'Industrial Automation & Control Systems',
      shortDesc: 'Advanced automation solutions for modern manufacturing',
      fullDesc: 'Custom automation systems designed to improve efficiency and reduce operational costs. Our solutions integrate seamlessly with existing infrastructure while providing enhanced monitoring and control capabilities.',
      features: [
        'Custom automation design',
        'PLC programming and integration',
        'SCADA system implementation',
        'Process optimization solutions',
        'Remote monitoring capabilities',
        'Predictive maintenance systems'
      ],
      standards: ['IEC Standards', 'Safety Integrity Levels', 'Industrial Protocols', 'Cybersecurity Compliance']
    },
    {
      icon: FiAward,
      title: 'Quality Assurance & Testing Services',
      shortDesc: 'Comprehensive testing protocols and quality management',
      fullDesc: 'Every component undergoes rigorous testing protocols to ensure compliance with specifications. Our quality assurance laboratory provides comprehensive material testing, dimensional verification, and certification services.',
      features: [
        'Material testing and analysis',
        'Dimensional inspection services',
        'Non-destructive testing (NDT)',
        'Pressure testing capabilities',
        'Certification and documentation',
        'Failure analysis services'
      ],
      standards: ['ISO 9001:2015', 'ASTM Testing Methods', 'EN Standards', 'Customer Specifications']
    },
    {
      icon: FiUsers,
      title: 'Custom Component Manufacturing',
      shortDesc: 'Tailored solutions for unique industrial requirements',
      fullDesc: 'Specialized manufacturing services for unique components that cannot be sourced from standard suppliers. From prototype development to full production runs, we deliver custom solutions that meet exact specifications.',
      features: [
        'Prototype development services',
        'Reverse engineering capabilities',
        'Custom tooling and fixtures',
        'Small to medium batch production',
        'Material selection consulting',
        'Design optimization support'
      ],
      standards: ['Customer Specifications', 'Industry Standards', 'Material Standards', 'Quality Protocols']
    },
    {
      icon: FiTrendingUp,
      title: 'Maintenance & Refurbishment Services',
      shortDesc: 'Expert maintenance and restoration of industrial equipment',
      fullDesc: 'Comprehensive maintenance and refurbishment services to extend equipment life and improve performance. Our experienced technicians provide on-site and workshop-based solutions for critical industrial equipment.',
      features: [
        'Preventive maintenance programs',
        'Emergency repair services',
        'Equipment refurbishment',
        'Performance upgrades',
        'Condition monitoring',
        'Spare parts manufacturing'
      ],
      standards: ['OEM Specifications', 'Safety Standards', 'Maintenance Protocols', 'Performance Metrics']
    }
  ];

  const testimonials = [
    {
      quote: "Gauteng Manufacturing's fabrication quality exceeded our expectations. The headgear refurbishment project was completed ahead of schedule with zero safety incidents.",
      author: "Project Manager",
      company: "Gold Mining Operation"
    },
    {
      quote: "Their precision machining capabilities allowed us to maintain production schedules despite complex component requirements. Quality consistency has been outstanding.",
      author: "Engineering Manager",
      company: "Automotive Assembly Plant"
    },
    {
      quote: "The custom automation systems delivered by Gauteng Manufacturing improved our processing efficiency by 28% whilst reducing maintenance requirements.",
      author: "Operations Director",
      company: "Chemical Processing Facility"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Our Services</h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto text-gray-200">
              Comprehensive manufacturing solutions backed by four decades of expertise 
              and state-of-the-art technology.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Service Navigation */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Service Categories</h2>
              <div className="space-y-2">
                {services.map((service, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveService(index)}
                    className={`w-full text-left p-4 rounded-lg transition-colors ${
                      activeService === index
                        ? 'bg-blue-800 text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <SafeIcon 
                        icon={service.icon} 
                        className={`text-xl ${activeService === index ? 'text-orange-400' : 'text-blue-800'}`} 
                      />
                      <span className="font-medium">{service.title}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Service Details */}
            <div className="lg:col-span-2">
              <motion.div
                key={activeService}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-gray-50 rounded-xl p-8"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={services[activeService].icon} className="text-2xl text-blue-800" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{services[activeService].title}</h3>
                    <p className="text-gray-600">{services[activeService].shortDesc}</p>
                  </div>
                </div>

                <p className="text-lg text-gray-700 mb-8">{services[activeService].fullDesc}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-4">Key Capabilities</h4>
                    <ul className="space-y-2">
                      {services[activeService].features.map((feature, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <SafeIcon icon={FiCheckCircle} className="text-green-500 text-sm" />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-4">Standards & Compliance</h4>
                    <ul className="space-y-2">
                      {services[activeService].standards.map((standard, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <SafeIcon icon={FiAward} className="text-orange-500 text-sm" />
                          <span className="text-gray-700 text-sm">{standard}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From initial consultation to final delivery, we follow a structured approach 
              that ensures quality, timeline adherence, and customer satisfaction.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'Understanding your requirements and technical specifications' },
              { step: '02', title: 'Design & Planning', desc: 'Detailed engineering design and project planning phase' },
              { step: '03', title: 'Manufacturing', desc: 'Precision manufacturing using advanced equipment and techniques' },
              { step: '04', title: 'Quality & Delivery', desc: 'Comprehensive testing, quality assurance, and timely delivery' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-800 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-full w-8 border-t-2 border-gray-300 z-0"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Client Testimonials</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from our satisfied clients about their experience with our manufacturing services.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 rounded-xl p-8"
              >
                <blockquote className="text-gray-700 mb-6 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="border-t border-gray-200 pt-4">
                  <div className="font-semibold text-gray-900">{testimonial.author}</div>
                  <div className="text-sm text-gray-600">{testimonial.company}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-6">Ready to Discuss Your Project?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Contact our engineering team to discuss your manufacturing requirements 
              and receive a detailed project proposal.
            </p>
            <button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2 text-lg">
              <span>Request Consultation</span>
              <SafeIcon icon={FiArrowRight} />
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;